/**
 * SoundOfTheWeek – a weekly pronunciation mini-drill card shown on Home.
 *
 * The card itself is cheap: we fetch `/api/sound-of-week` (static metadata)
 * on mount. The 5 LLM-generated practice phrases are only fetched on click
 * — that's where the LLM bill is, and most users won't tap it every day.
 *
 * Interactions:
 *   - Tap "Hear it" to TTS the focus word.
 *   - Tap "Start drill" to expand the 5 practice phrases, each with respell
 *     + IPA + a "Play" button that speaks the target.
 *
 * Lightweight by design — no scoring here (it's a discovery card, not the
 * full Repeat Practice module).
 */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MusicNotes, SpeakerHigh, SpinnerGap, CaretDown, CaretUp, Play,
} from '@phosphor-icons/react';
import { http, API, apiErrorMessage, getStoredToken } from '@/lib/api';

export default function SoundOfTheWeek() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [drill, setDrill] = useState(null);
  const [loadingDrill, setLoadingDrill] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get('/sound-of-week');
        setData(data);
      } catch (e) {
        setError(apiErrorMessage(e));
      }
    })();
  }, []);

  async function toggleDrill() {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (drill) return;
    setLoadingDrill(true);
    try {
      const { data } = await http.post('/sound/drill');
      setDrill(data);
    } catch (e) { setError(apiErrorMessage(e)); }
    finally { setLoadingDrill(false); }
  }

  async function speak(id, text) {
    if (!text) return;
    setPlayingId(id);
    try {
      const resp = await fetch(`${API}/tts`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(getStoredToken() ? { Authorization: `Bearer ${getStoredToken()}` } : {}),
        },
        body: JSON.stringify({ text, speed: 0.85 }),
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => { URL.revokeObjectURL(url); setPlayingId(null); };
      audio.onerror = () => { URL.revokeObjectURL(url); setPlayingId(null); };
      audio.play().catch(() => setPlayingId(null));
    } catch { setPlayingId(null); }
  }

  if (error || !data?.sound) return null;
  const s = data.sound;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-6 lf-card overflow-hidden"
      data-testid="home-sound-of-week"
    >
      <div className="p-5">
        <div className="flex items-center gap-2">
          <MusicNotes size={18} weight="duotone" className="text-[var(--lf-primary)]" />
          <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">
            Sound of the week
          </div>
        </div>
        <div className="mt-1 text-xl font-bold" data-testid="sow-name">{s.name}</div>
        <div className="text-sm text-[var(--lf-text-muted)]">
          <span className="font-semibold">{s.short}</span> · IPA <span className="font-mono">/{s.ipa}/</span>
        </div>
        <p className="mt-2 text-sm">{s.description}</p>
        {s.tip ? (
          <div className="mt-2 rounded-2xl bg-[var(--lf-surface-muted)] p-3 text-xs italic text-[var(--lf-text)]">
            💡 {s.tip}
          </div>
        ) : null}
        {s.example_words?.length ? (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="sow-examples">
            {s.example_words.map((w) => (
              <button
                key={w}
                onClick={() => speak(`ex-${w}`, w)}
                className="inline-flex items-center gap-1 rounded-full bg-[var(--lf-surface)] border border-[var(--lf-border)] px-3 py-1 text-xs font-semibold hover:bg-[var(--lf-surface-muted)]"
                data-testid={`sow-ex-${w}`}
              >
                {playingId === `ex-${w}`
                  ? <SpinnerGap size={10} className="animate-spin" />
                  : <SpeakerHigh size={10} weight="fill" />}
                {w}
              </button>
            ))}
          </div>
        ) : null}

        <button
          onClick={toggleDrill}
          className="lf-btn lf-btn-primary mt-4 w-full py-2 text-sm"
          data-testid="sow-toggle-drill-btn"
        >
          {open ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
          {open ? 'Hide drill' : 'Start 5-phrase drill'}
        </button>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key="drill"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-3 space-y-2 overflow-hidden"
              data-testid="sow-drill"
            >
              {loadingDrill ? (
                <div className="p-3 text-xs text-[var(--lf-text-muted)]">
                  <SpinnerGap size={12} className="inline animate-spin" /> Warming up the AI coach…
                </div>
              ) : null}
              {(drill?.phrases || []).map((ph, i) => (
                <div
                  key={`${ph.target}-${i}`}
                  className="rounded-2xl border border-[var(--lf-border)] bg-[var(--lf-surface)] p-3"
                  data-testid={`sow-phrase-${i}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="font-semibold">{ph.target}</div>
                      {ph.respelling ? (
                        <div className="mt-0.5 text-xs text-[var(--lf-text-muted)]">
                          <span className="font-mono">{ph.respelling}</span>
                          {ph.ipa ? <span className="ml-2 font-mono">/{ph.ipa}/</span> : null}
                        </div>
                      ) : null}
                      <div className="mt-1 text-xs text-[var(--lf-text)]">{ph.native}</div>
                      {ph.tip ? <div className="mt-1 text-xs italic text-[var(--lf-text-muted)]">{ph.tip}</div> : null}
                    </div>
                    <button
                      onClick={() => speak(`ph-${i}`, ph.target)}
                      className="grid h-8 w-8 place-items-center rounded-full bg-[var(--lf-primary)] text-white shrink-0"
                      data-testid={`sow-phrase-play-${i}`}
                    >
                      {playingId === `ph-${i}`
                        ? <SpinnerGap size={12} className="animate-spin" />
                        : <Play size={12} weight="fill" />}
                    </button>
                  </div>
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
